import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { userApplicationApi } from '@/lib/api/questionnaireApi';
import { QuestionProps, UserApplicationData } from '@/types/questionnaire';
import { router } from '@inertiajs/react';
import { useQuery } from '@tanstack/react-query';
import { UserPlus, Info } from 'lucide-react';
import { useState } from 'react';

const getOptionLabel = (question: QuestionProps, value: string) => {
  if (!('options' in question) || !question.options) return value;
  const option = question.options.find((opt) => opt.value === value);
  return option?.label || value;
};

// Custom hook to fetch mentee profile data with mentor visibility filter
const useMenteeProfile = (menteeId: number | null) => {
  return useQuery<UserApplicationData>({
    queryKey: ['mentee-profile', menteeId],
    queryFn: async () => {
      if (!menteeId) throw new Error('No mentee ID provided');
      // This will need a new endpoint that filters for mentor-visible questions
      return await userApplicationApi.getMenteeProfile(menteeId);
    },
    enabled: !!menteeId,
  });
};

interface MenteeProfileProps {
  menteeId: number | null;
  onClose: () => void;
  isMatched?: boolean;
}

export default function MenteeProfile({
  menteeId,
  onClose,
  isMatched = false,
}: MenteeProfileProps) {
  const { data: profileData, isLoading } = useMenteeProfile(menteeId);
  const [isMatching, setIsMatching] = useState(false);

  const handleCreateMatch = async () => {
    if (!menteeId) return;

    setIsMatching(true);
    router.post(
      '/matches',
      { mentee_id: menteeId },
      {
        preserveScroll: true,
        onFinish: () => {
          setIsMatching(false);
        },
        onSuccess: () => {
          onClose();
          // onMatch?.();
        },
        onError: (errors) => {
          console.error('Failed to create match:', errors);
        },
      }
    );
  };

  return (
    <Dialog open={!!menteeId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0 flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Mentee Profile
          </DialogTitle>
          {profileData && (
            <div className="mt-3 space-y-1">
              <div className="text-sm text-gray-900 font-medium">
                {`${profileData.user.first_name} ${profileData.user.last_name}.`}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span className="capitalize">
                  {profileData.user.category.replace('_', ' ')} Mentee
                </span>
                {profileData.completed_at && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span>
                      Applied{' '}
                      {new Date(profileData.completed_at).toLocaleDateString()}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div className="text-center py-8 text-gray-500">
              Loading profile...
            </div>
          )}

          {profileData && profileData.sections && (
            <>
              {/* Privacy Notice */}
              <Alert className="mb-6">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  This profile shows information the mentee has chosen to share
                  with mentors. Some personal details may be hidden for privacy.
                </AlertDescription>
              </Alert>

              <div className="space-y-6">
                {profileData.sections.map((section, sectionIndex) => {
                  // Filter to only show mentor-visible questions
                  const visibleQuestions = section.questions.filter(
                    (q) => q.is_visible_to_mentors
                  );

                  // Skip section if no visible questions
                  if (visibleQuestions.length === 0) return null;

                  return (
                    <div key={section.id}>
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {section.section_name}
                        </h3>
                        {section.description && (
                          <p className="text-sm text-gray-500 mt-1">
                            {section.description}
                          </p>
                        )}
                      </div>

                      <div className="space-y-4 ml-4">
                        {visibleQuestions.map((question) => (
                          <div key={question.id} className="group">
                            <div className="flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700 mb-1">
                                  {question.label}
                                </p>
                                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                                  {question.response_value ? (
                                    Array.isArray(question.response_value) ? (
                                      <ul className="space-y-1">
                                        {question.response_value.map(
                                          (item, idx) => (
                                            <li
                                              key={idx}
                                              className="flex items-center gap-2"
                                            >
                                              <span className="text-gray-400">
                                                →
                                              </span>
                                              {getOptionLabel(question, item)}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    ) : question.question_type === 'text' ? (
                                      <div className="whitespace-pre-wrap">
                                        {question.response_value}
                                      </div>
                                    ) : question.question_type === 'select' ||
                                      question.question_type === 'radio' ? (
                                      getOptionLabel(
                                        question,
                                        question.response_value
                                      )
                                    ) : (
                                      question.response_value
                                    )
                                  ) : (
                                    <span className="text-gray-400 italic">
                                      No response provided
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {sectionIndex < profileData.sections.length - 1 && (
                        <div className="border-b border-gray-200 mt-6" />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {profileData && (
          <div
            className="px-6 py-3 border-t flex justify-between items-center
          flex-shrink-0"
          >
            <div className="text-xs text-gray-500">
              Mentee ID: #{profileData.user.id}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {!isMatched && (
                <Button
                  onClick={handleCreateMatch}
                  disabled={isMatching}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {isMatching ? 'Creating Match...' : 'Match with Mentee'}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
